import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Parse a flexible time string into HH:MM:SS (24-hour) format.
 *
 * Examples:
 *   "3pm" / "3 pm"       → "15:00:00"
 *   "3am" / "3 am"       → "03:00:00"
 *   "3"                   → "03:00:00"
 *   "14"                  → "14:00:00"
 *   "3:30pm"              → "15:30:00"
 *   "3:30 am"             → "03:30:00"
 *   "15:30"               → "15:30:00"
 *   "12pm"                → "12:00:00"
 *   "12am"                → "00:00:00"
 */
export function parseTimeInput(raw: string): string | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;

  // Try 3-4 digit military time without colon (e.g. 0900, 900, 1530)
  const milMatch = s.match(/^(\d{3,4})$/);
  if (milMatch) {
    const digits = milMatch[1].padStart(4, '0');
    const h = parseInt(digits.slice(0, 2), 10);
    const m = parseInt(digits.slice(2), 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
    }
    return null;
  }

  // Match: optional hours, optional :minutes, optional am/pm
  const match = s.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm|a|p)?$/);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const meridiem = match[3]; // am/pm/a/p or undefined

  if (minutes < 0 || minutes > 59) return null;

  if (meridiem) {
    // 12-hour format
    if (hours < 1 || hours > 12) return null;
    const isPM = meridiem === 'pm' || meridiem === 'p';
    if (hours === 12) {
      hours = isPM ? 12 : 0;
    } else {
      hours = isPM ? hours + 12 : hours;
    }
  } else {
    // No meridiem — treat as 24-hour if > 12, else AM
    if (hours < 0 || hours > 23) return null;
  }

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  return `${hh}:${mm}:00`;
}

/**
 * Format HH:MM:SS (24-hour) to display string like "3:00 PM"
 */
function formatTimeDisplay(timeStr: string | null): string {
  if (!timeStr) return '';
  const [hStr, mStr] = timeStr.split(':');
  let hours = parseInt(hStr, 10);
  const minutes = parseInt(mStr, 10);
  if (isNaN(hours) || isNaN(minutes)) return '';

  const meridiem = hours >= 12 ? 'PM' : 'AM';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;

  const mm = String(minutes).padStart(2, '0');
  return `${hours}:${mm} ${meridiem}`;
}

interface TimeInputProps {
  value: string | null;
  onChange: (timeStr: string | null) => void;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
}

export default function TimeInput({ value, onChange, disabled, onFocus, onBlur, placeholder = 'Time' }: TimeInputProps) {
  const [inputValue, setInputValue] = useState(() => formatTimeDisplay(value));
  const [isFocused, setIsFocused] = useState(false);
  const prevValueRef = useRef(value);

  // Sync display when external value changes (but not while user is typing)
  useEffect(() => {
    if (!isFocused && value !== prevValueRef.current) {
      setInputValue(formatTimeDisplay(value));
      prevValueRef.current = value;
    }
  }, [value, isFocused]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();

    const trimmed = inputValue.trim();
    if (!trimmed) {
      // Cleared the field
      if (value) {
        onChange(null);
      }
      setInputValue('');
      return;
    }

    const parsed = parseTimeInput(trimmed);
    if (parsed) {
      setInputValue(formatTimeDisplay(parsed));
      if (parsed !== value) {
        onChange(parsed);
      }
    } else {
      // Invalid input — revert to previous value
      setInputValue(formatTimeDisplay(value));
    }
  }, [inputValue, value, onChange, onBlur]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  }, []);

  return (
    <input
      type="text"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-heading placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
    />
  );
}
